import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, Users, BarChart3 } from 'lucide-react';
import { fetchAnalytics } from '../../services/MasterAdmin/marketingEmailService';
import type { MarketingEmailAnalytics } from '../../models/MasterAdmin/marketingEmail';

const MarketingDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<MarketingEmailAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchAnalytics();
        if (!cancelled && res.success) {
          setAnalytics(res.data);
        }
      } catch {
        if (!cancelled) setAnalytics(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const stats = [
    {
      label: 'Emails sent',
      value: analytics?.totalRecipientsSent ?? '—',
      icon: <Send className="w-5 h-5 text-primary-600 dark:text-primary-300" />,
    },
    {
      label: 'Saved contacts',
      value: analytics?.totalContacts ?? '—',
      icon: <Users className="w-5 h-5 text-primary-600 dark:text-primary-300" />,
    },
    {
      label: 'Campaigns',
      value: analytics?.totalCampaigns ?? '—',
      icon: <BarChart3 className="w-5 h-5 text-primary-600 dark:text-primary-300" />,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Marketing Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Compose campaigns, manage contacts, and track email performance.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</span>
              <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                {stat.icon}
              </div>
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white">
              {loading ? '…' : stat.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/marketing/emails')}
          className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all"
        >
          <div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-primary-600 dark:text-primary-300" />
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">Marketing Emails</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Compose, send, and manage email campaigns with templates and signatures.
          </div>
        </button>
      </div>
    </div>
  );
};

export default MarketingDashboardPage;
